import { HttpClient } from "@angular/common/http";
import { APP_INITIALIZER, inject, Injectable, LOCALE_ID } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { SummaryType } from "src/app/common/services/summary-label.service";
import { UserNeed, userNeedData } from "src/app/config";
import { EntityWikidataStatus } from "src/app/modules/entity/services/entity.service";
import { PredefinedPrompt } from "src/app/modules/prompts/models/prompt-template.model";

export interface UserNeedsValue {
  label: string;
  description?: string;
  icon?: string;
}

export type UserNeeds = Record<string, UserNeedsValue>;

export type MediaItemType =
  | "CLASSIFIED_ARTICLE"
  | "SPEECH2TEXT"
  | "AGENCY_NEWS"
  | "AUDIOVISUAL_MEDIA"
  | "INSTAGRAM_POST";

export type SearchResultType =
  | "ARTICLE"
  | "SPEECH2TEXT_AUDIO"
  | "SPEECH2TEXT_VIDEO"
  | "AUDIOVISUAL_MEDIA_AUDIO"
  | "AUDIOVISUAL_MEDIA_VIDEO"
  | "AGENCY_NEWS"
  | "INSTAGRAM_POST";

interface BackendUserNeed {
  userNeed: UserNeed;
  userNeedDetails: {
    label: string;
    description?: string;
    icon?: string;
  };
  mediaItemTypes: MediaItemType[];
}

export interface LlmBackendConfig {
  technicalName: string;
  contextWindow: number;
}

const API_PATH = "/config";

@Injectable({
  providedIn: "root",
})
export class ConfigService {
  environment!: "dev" | "exp" | "int" | "local" | "prod";
  apiMl!: { httpUrl: string; webSocketUrl: string };
  scraping!: { httpUrl: string };
  buildVersion?: string;
  entityWikidataStatus!: EntityWikidataStatus[];
  roles!: string[];
  audioBucket!: string;
  cloudfrontUrl!: string;
  llmConfigs!: Record<string, LlmBackendConfig>;
  technicalNamesToContextWindows!: Record<string, number>;
  defaultLlm!: string;
  largestLlm!: string;
  searchLlm!: string;
  imageLlm!: string;
  summaryPrompts!: Record<SummaryType, PredefinedPrompt>;
  userneedsPerType: Record<MediaItemType, UserNeeds> = {
    CLASSIFIED_ARTICLE: {},
    SPEECH2TEXT: {},
    AGENCY_NEWS: {},
    AUDIOVISUAL_MEDIA: {},
    INSTAGRAM_POST: {},
  };

  userNeedsEntriesPerType: Record<MediaItemType, [string, UserNeedsValue][]> = {
    CLASSIFIED_ARTICLE: [],
    SPEECH2TEXT: [],
    AGENCY_NEWS: [],
    AUDIOVISUAL_MEDIA: [],
    INSTAGRAM_POST: [],
  };

  userNeedsKeysPerType: Record<MediaItemType, string[]> = {
    CLASSIFIED_ARTICLE: [],
    SPEECH2TEXT: [],
    AGENCY_NEWS: [],
    AUDIOVISUAL_MEDIA: [],
    INSTAGRAM_POST: [],
  };

  instagramProfiles!: Record<number, string>;

  private readonly httpClient = inject(HttpClient);
  private readonly locale = inject(LOCALE_ID);
  private readonly userNeedData = userNeedData;

  async init(): Promise<void> {
    Object.assign(this, await firstValueFrom(this.httpClient.get(API_PATH)));
    this.summaryPrompts = await firstValueFrom(
      this.httpClient.get<Record<SummaryType, PredefinedPrompt>>(`${API_PATH}/summaryPrompts/${this.locale}`),
    );

    this.instagramProfiles = await firstValueFrom(
      this.httpClient.get<Record<number, string>>(`${API_PATH}/instagramProfiles`),
    );

    const backendUserNeeds = await firstValueFrom(this.httpClient.get<BackendUserNeed[]>(`${API_PATH}/user-needs`));
    for (const backend of backendUserNeeds) {
      for (const type of backend.mediaItemTypes) {
        const userNeedsValue: UserNeedsValue = {
          label: userNeedData[backend.userNeed].label,
          description: userNeedData[backend.userNeed].description,
          icon: userNeedData[backend.userNeed].icon,
        };
        this.userneedsPerType[type][backend.userNeed] = userNeedsValue;
        this.userNeedsEntriesPerType[type].push([backend.userNeed, userNeedsValue]);
        this.userNeedsKeysPerType[type].push(backend.userNeed);
      }
    }
  }

  async update(id: string, value: string): Promise<void> {
    await firstValueFrom(this.httpClient.put(`${API_PATH}/${id}`, value));
  }
}

export const configServiceInitializerFactory = (config: ConfigService) => {
  return async (): Promise<void> => {
    await config.init();
  };
};

export const configServiceInitializer = {
  provide: APP_INITIALIZER,
  useFactory: configServiceInitializerFactory,
  deps: [ConfigService],
  multi: true,
};
